import { RemovalRequest, RemovalResult } from "../interfaces/DTO/removal";
// services/simulation/SimulationService.ts

import apiClient from "../core/apiClient";
import { CreateQuickSimulationRequest, CreateQuickSimulationResponse, CreateSimulationRequest, Simulation } from "../interfaces/DTO/simulation";
import { BaseService } from "./BaseService";
import {
  CreateSimulationPortalLinkRequest,
  SimulationPortalLinkResponseDTO,
} from "../interfaces/DTO/public/simulationPortalLink";



const API_URL = "/simulations";
// Served by a separate backend router (simulation_portal_access_router.py) so it
// doesn't share the /simulations namespace with the plain Simulation CRUD router.
const PORTAL_ACCESS_API_URL = "/simulations-portal-access";

class SimulationService extends BaseService<Simulation> {
  constructor() {
    super(API_URL);
  }
  async createWithSnapshot(dto: CreateSimulationRequest): Promise<Simulation> {
    const response = await apiClient.post<Simulation>(`${API_URL}/create-with-snapshot`, dto);
    return response.data;
  }

  async fetchByContactAndPeriod(contact_id: number, reported_period: string): Promise<Simulation[]> {
    const response = await apiClient.get<Simulation[]>(`${API_URL}/by-user`, {
      params: { contact_id: contact_id, reported_period },
    });
    return response.data;
  }
  async fetchByContactsAndPeriod(
    contact_ids: number[],
    reported_period?: string
  ): Promise<Simulation[]> {
    const response = await apiClient.get<Simulation[]>(`${API_URL}/by-users`, {
      params: {
        contact_ids, // axios יתמוך במערכים → ?contact_ids=1&contact_ids=2
        ...(reported_period ? { reported_period } : {}),
      },
      paramsSerializer: (params) => {
        const query = new URLSearchParams();
        if (params.contact_ids) {
          (params.contact_ids as number[]).forEach((id) =>
            query.append("contact_ids", id.toString())
          );
        }
        if (params.reported_period) {
          query.append("reported_period", params.reported_period);
        }
        return query.toString();
      },
    });
    return response.data;
  }
  async quickCreate(dto: CreateQuickSimulationRequest): Promise<CreateQuickSimulationResponse> {
    const response = await apiClient.post<CreateQuickSimulationResponse>(`${API_URL}/quick-create`, dto);
    return response.data;
  }

  async patchMetadata(id: number, fields: { name?: string; notes?: string; status?: string; data?: Record<string, any> }): Promise<Simulation> {
    const response = await apiClient.patch<Simulation>(`${API_URL}/${id}`, fields);
    return response.data;
  }

  /** אישור סימולציה לביצוע (SELECTED → APPROVED) – יוצר משימות משורות הסימולציה כפי שנשמרו */
  async approve(id: number): Promise<CreateQuickSimulationResponse> {
    const response = await apiClient.post<CreateQuickSimulationResponse>(`${API_URL}/${id}/approve`);
    return response.data;
  }

  /** deleted when none of it left the office, otherwise discarded with its open tasks cancelled (backend TaskRemovalService) */
  async removeSimulation(id: number, request: RemovalRequest): Promise<RemovalResult> {
    const response = await apiClient.post<RemovalResult>(`${API_URL}/${id}/remove`, request);
    return response.data;
  }

  async getPortalLink(simulationId: number): Promise<SimulationPortalLinkResponseDTO | null> {
    const response = await apiClient.get(`${PORTAL_ACCESS_API_URL}/portal-link/${simulationId}`);
    return response.data ?? null;
  }

  async createPortalLink(
    simulationId: number,
    body: CreateSimulationPortalLinkRequest = {}
  ): Promise<SimulationPortalLinkResponseDTO> {
    const response = await apiClient.post(`${PORTAL_ACCESS_API_URL}/portal-link/${simulationId}`, body);
    return response.data;
  }

  async revokePortalLink(simulationId: number): Promise<void> {
    await apiClient.delete(`${PORTAL_ACCESS_API_URL}/portal-link/${simulationId}`);
  }
}

export const simulationService = new SimulationService();
